<template>
  <div
    class="v-search-map v-search-page-map"
    :class="{loading: isMobile && loading}"
    ref="searchMap"
  >
    <div
      v-show="loadingDelayed"
      class="v-search-map__loading shadow-elevation-large"
    >
      <img
        width="32"
        height="12"
        :src="'/img/three-dots-loading.svg'"
        alt="Loading..."
      >
    </div>

    <div
      class="v-search-map__map"
      ref="mapContainer"
    />

    <template v-if="isMobile">
      <div v-if="!loadingDelayed"
        class="fixed bg-white py-2 px-3 rounded-xl text-center left-1/2 z-999 text-sm flex items-center" style="bottom: 28px; transform: translateX(-50%); white-space: nowrap;"
        @click="venuesInAreaClick"
      >
        <SearchResultsMetaHeader :mobile-view="true" />
      </div>

      <div v-if="isLocateMeSupported" class="absolute right-0 top-0 z-999">
        <BaseButton color="icon" class="w-10 h-10 p-0 flex items-center justify-center mr-2 mt-2" @click="locateMe">
          <LocateMeIcon class="w-5 h-5" />
        </BaseButton>
      </div>

      <div v-if="isLocateMeError" class="fixed left-0 right-0 top-0 z-999 ml-2 mr-14 mt-2">
        <BaseNotification type="warning" layout="inline">
          <div class="ml-1">
            Please enable location permission in your browser settings.
          </div>
        </BaseNotification>
      </div>
      <div ref="infoWindowContainer" class="info-window-container fixed bottom-0 rounded-xl mb-6 flex items-center justify-center bg-white shadow-lg overflow-hidden none">
        <div class="absolute top-0 left-0 mt-3 ml-3 z-999 w-6 h-6 info-window-close" @click="closeInfoWindow" />
        <div ref="infoWindowContent" class="w-full" />
      </div>
    </template>
  </div>
</template>

<script>
import LocateMeIcon from '@tagvenue/icons/24/line/maps-travel/navigation-pointer-01.svg';
import debounce from 'debounce';
import { mapGetters, mapState } from 'vuex';

import BaseNotification from '@/components/Ui/BaseNotification.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import { visibilityTracker } from '@/modules/public/search/data-tracking/search-visibility-tracker';
import { InfoWindow, infoWindowSpinner, infoWindowTemplate } from '@/modules/public/search/map.js';
import { getRoomData, waitForGoogleMaps } from '@/modules/public/search/services.js';
import { preloadUrl } from '@/services/room-card-prefetcher';
import { sleep } from '@/utils';

import SearchResultsMetaHeader from '../SearchResultsMetaHeader.vue';
import { getZoomControl } from './SearchControls.js';

// all marker instances created with new Map, every marker is created once and later reused
const addedVenuesMarkers = {};

let addedVenuesMarkersZoom = null;

// {venueId: bool} map of venues/markers that are currently visible on the screen
// if marker was added and hid it will be listed with false value
const visibleVenueMarkers = {};

// helper object to calculate transitions between points updates
// contains only visible elements, reassign on every points update
let currentVenueMarkers = {};

// points to be rendered on the map
let initialMapPoints = window._VUE_SEARCH_DATA.allRoomsPositions;

function getInitialMapBounds() {
  const queryParams = window._VUE_SEARCH_CONFIG.queryParams;

  // @todo TM-2354 restore shrinking after BE bugs are fixed
  return {
    west: queryParams.longitude_from,
    east: queryParams.longitude_to,
    south: queryParams.latitude_from,
    north: queryParams.latitude_to,
  };
}

let initialMapBounds = getInitialMapBounds();

const highlightedMarker = {
  marker: null,
  venueId: null,
};

const openedMarker = {
  marker: null,
  venueId: null,
  roomId: null,
};

const ICONS = {
  red: '/img/icons/pin-red.svg',
  blue: '/img/icons/pin-blue.svg',
};

const SETTINGS = {
  // pins grid settings
  // how many columns/rows should be in our grid
  STEP_NR: 18,
  MARKERS_IN_CELL: 1,
  SHOW_EVERY: 70,
  MINIMUM_ZOOM: 15,
};

// minimum number of markers to use grid
// if it's less don't use grid mechanism
// const MINIMUM_MARKERS = 200;

// array of map bounds with painted pins
// after panning we will not put any pins if a pin is in this array
let PAINTED_PINS_BOUNDS = [];

window.addedVenuesMarkers = addedVenuesMarkers;
window.visibleVenueMarkers = visibleVenueMarkers;
window.currentVenueMarkers = currentVenueMarkers;

export default {
  name: 'SearchMap',
  components: { BaseNotification, BaseButton, SearchResultsMetaHeader, LocateMeIcon },

  data() {
    return {
      MAX_ZOOM: 20,
      MAX_ZOOM_FROM_LOCATION_INPUT: 16,
      loadingDelayed: false,
      loadingDelayedTimeoutId: null,
      mapContainer: [],
      isInitialBoundChange: true, // ignore initial bounds change
      ignoreBoundsChange: false, // ignore initial bounds change
      mapInitiated: false,
      loadMapTriggered: false,
      googleMapLoaded: false,
      isMapResetTrigger: false,
      isLocateMeError: false,
      gMapMarkerApi: null,
      infowindow: null,
    };
  },
  computed: {
    ...mapState([
      'isMobile',
      'mobileView',
      'loading',
      'rooms',
      'page',
      'eventType',
      'eventTypeSlug',
      'toggle',
    ]),
    ...mapGetters([
      'resultListVenueIds',
    ]),
    isLocateMeSupported() {
      return 'geolocation' in navigator;
    },
  },
  watch: {
    // Turning a tablet swaps the mobile and desktop layouts. The mobile info window writes into markup that only the
    // mobile layout renders, so the instance built at load kept filling a detached element after a turn and back.
    isMobile() {
      this.hideTooltip();

      if (!this.mapInitiated) {
        return;
      }

      this.$nextTick(() => {
        this.infowindow = this.createInfoWindow();
      });
    },
    mobileView: {
      immediate: true,
      handler() {
        this.hideTooltip();
        this.unHighlightPins();
        this.onMobileViewChange();
      },
    },
    // set loadingDelayed that always takes minimum 300ms
    loading() {
      if (this.loading === true) {
        this.loadingDelayed = true;
        this.loadingDelayedTimeoutId = setTimeout(() => {
          if (this.loading === false) {
            this.loadingDelayed = false;
          }
          this.loadingDelayedTimeoutId = null;
        }, 700);
        return;
      }
      if (this.loading === false && this.loadingDelayedTimeoutId === null) {
        this.loadingDelayed = false;
      }
    },
  },
  methods: {
    getMarkerContent() {
      const el = document.createElement('img');
      el.src = ICONS.red;
      return el;
    },
    onMobileViewChange() {
      if (this.isMobile && this.mobileView === 'map') {
        this.ignoreTemporaryBoundsChange(); // avoid number of results reload when changing view
      }
    },
    isPointOnTCurrentResultList(point) {
      return this.isVenueOnCurrentResultList(+point.venue_id);
    },
    isVenueOnCurrentResultList(venueId) {
      return this.resultListVenueIds.indexOf(venueId) !== -1;
    },
    // algo to reduce number of pins displayed on the map in a way that:
    // show pins if there is enough space
    // shows "hotspots" of some areas are more popular it should be indicated, but still without showing all pins
    // what would be too much
    // at some zoom level, show all points.
    filterPoints(points, bounds) {
      const densityMatrix = [];
      const horizontalSteps = [];
      const verticalSteps = [];
      const boundsJSON = bounds.toJSON();

      if (this.gMap.getZoom() >= SETTINGS.MINIMUM_ZOOM) {
        return points;
      }

      const horizontalStep = (boundsJSON.east - boundsJSON.west) / SETTINGS.STEP_NR;
      const verticalStep = (boundsJSON.north - boundsJSON.south) / SETTINGS.STEP_NR;

      // generate density grid
      for (let i = 0; i < SETTINGS.STEP_NR; i += 1) {
        const x = boundsJSON.west + ((i + 1) * horizontalStep);
        const y = boundsJSON.south + ((i + 1) * verticalStep);
        horizontalSteps.push(x);
        verticalSteps.push(y);
        densityMatrix[i] = [];
        for (let j = 0; j < SETTINGS.STEP_NR; j += 1) {
          densityMatrix[i][j] = 0;
        }
      }

      // return number of item in a grid cell
      function getInMatrix(x, y) {
        for (let i = 0; i < SETTINGS.STEP_NR; i += 1) {
          for (let j = 0; j < SETTINGS.STEP_NR; j += 1) {
            if (x < horizontalSteps[i] && y < verticalSteps[j]) {
              densityMatrix[i][j] += 1;
              return densityMatrix[i][j] - 1;
            }
          }
        }
        // should never happen if it does - show the marker
        return 0;
      }

      return points.filter((point) => {
        for (let i = 0; i < PAINTED_PINS_BOUNDS.length; i += 1) {
          if (PAINTED_PINS_BOUNDS[i].contains({
            lat: point.position.latitude,
            lng: point.position.longitude,
          })) {
            return false;
          }
        }

        // always show pins for current results items
        if (this.isPointOnTCurrentResultList(point)) {
          return true;
        }

        const count = getInMatrix(point.position.longitude, point.position.latitude);
        if (count < SETTINGS.MARKERS_IN_CELL || count % SETTINGS.SHOW_EVERY === 0) {
          return true;
        }
        return false;
      });
    },
    getVenuePinZIndex(venueId) {
      return this.isVenueOnCurrentResultList(venueId) ? 2 : 1;
    },
    updateZIndex(marker, venueId) {
      marker.zIndex = this.getVenuePinZIndex(venueId);
    },
    updatePoints(points, trigger) {
      const zoom = this.gMap.getZoom();
      if ((addedVenuesMarkersZoom && addedVenuesMarkersZoom !== zoom) || trigger !== 'map') {
        this.resetPaintedPins();
      }

      currentVenueMarkers = {};

      const bounds = this.gMap.getBounds();

      // If map not ready yet ignore it
      if (!bounds) {
        return;
      }

      const filteredPoints = this.filterPoints(points, bounds);

      filteredPoints.forEach((point) => {
        if (addedVenuesMarkers[point.venue_id] && visibleVenueMarkers[point.venue_id]) {
          this.updateZIndex(addedVenuesMarkers[point.venue_id], point.venue_id);
          currentVenueMarkers[point.venue_id] = true;
          return;
        }

        if (addedVenuesMarkers[point.venue_id] && !visibleVenueMarkers[point.venue_id]) {
          addedVenuesMarkers[point.venue_id].content.style.visibility = 'visible';
          this.updateZIndex(addedVenuesMarkers[point.venue_id], point.venue_id);
          currentVenueMarkers[point.venue_id] = true;
          visibleVenueMarkers[point.venue_id] = true;
          return;
        }

        // this.getPinIcon(point.venue_id),

        const newMarker = new window.google.maps.marker.AdvancedMarkerElement({
          map: this.gMap,
          position: {
            lat: point.position.latitude,
            lng: point.position.longitude,
          },
          content: this.getMarkerContent(),
          zIndex: this.getVenuePinZIndex(point.venue_id),
          gmpClickable: true,
        });

        addedVenuesMarkers[point.venue_id] = newMarker;
        visibleVenueMarkers[point.venue_id] = true;
        currentVenueMarkers[point.venue_id] = true;

        $(this.$refs.mapContainer).data('E2ETestAddedVenuesMarkers', addedVenuesMarkers);

        newMarker.addEventListener('gmp-click', () => {
          // ignore if already opened for same room
          if (openedMarker.roomId === point.room_id) {
            return;
          }
          this.unHighlightPins();
          this.openTooltip(newMarker, infoWindowSpinner(), point.venue_id, point.room_id);
          this.getRoomData(this.getTooltipRoomId(point), (err, data) => {
            this.infowindow.setContent(infoWindowTemplate(data));
            setTimeout(() => {
              this.initTooltipCarousel();
            });
            preloadUrl(data.data.room_url);
          });
          this.highlightPin(point.venue_id);
          this.trackPinClick(point);
        });

        newMarker.content.addEventListener('mouseover', () => {
          this.highlightPin(point.venue_id);
          this.getRoomData(this.getTooltipRoomId(point));
        });

        newMarker.content.addEventListener('mouseout', () => {
          this.unHighlightPins();
        });
      });

      // hide pins only if we zoomed in/out or when we pan above SETTINGS.MINIMUM_ZOOM value
      // only above SETTINGS.MINIMUM_ZOOM all pins are visible and we can preserve them
      if (addedVenuesMarkersZoom !== zoom || trigger !== 'map') { // || zoom < SETTINGS.MINIMUM_ZOOM
        Object.keys(visibleVenueMarkers).forEach((key) => {
          if (!currentVenueMarkers[key]) {
            addedVenuesMarkers[key].content.style.visibility = 'hidden';
            delete visibleVenueMarkers[key];
          }
        });
      }

      PAINTED_PINS_BOUNDS.push(bounds);

      addedVenuesMarkersZoom = zoom;
    },
    onMove() {
      if (this.isMobile && this.mobileView === 'map') {
        this.infowindow.close();
      }

      this.setNewBounds();
    },
    getTooltipRoomId(room) {
      const featuredRoomInVenue = this.rooms.filter(el => el.venue_id === room.venue_id && el.is_featured_room);

      if (featuredRoomInVenue.length) {
        return +featuredRoomInVenue[0].room_id;
      }

      return +room.room_id;
    },
    setNewBounds() {
      if (this.ignoreBoundsChange) {
        this.ignoreBoundsChange = false;
        return;
      }

      const bounds = this.gMap.getBounds();

      if (!bounds) {
        return;
      }

      this.$store.dispatch('mapBoundariesChanged', {
        ...bounds.toJSON(),
        initial: this.isInitialBoundChange && !this.isMobile,
        isMapResetTrigger: this.isMapResetTrigger,
      });

      this.isInitialBoundChange = false;
      this.isMapResetTrigger = false;
    },
    adjustMapTooltip() {
      $('.info-box').closest('.gm-style-iw').parent().addClass('info-box-wrapper');
    },
    openTooltip(marker, content, venueId, roomId) {
      if (openedMarker.marker) {
        this.unHighlightPin(openedMarker);
      }
      openedMarker.marker = marker;
      openedMarker.venueId = venueId;
      openedMarker.roomId = roomId;

      this.ignoreTemporaryBoundsChange();

      this.infowindow.setContent(content);

      this.infowindow.open(this.gMap, marker);

      // sometimes element appears sync, sometimes async
      // we try to do it sync and later async as is causes no side effects
      this.adjustMapTooltip();
      setTimeout(this.adjustMapTooltip);

      $('.v-search-rooms-component').one('click', () => {
        this.hideTooltip();
      });
    },
    initTooltipCarousel() {
      const carousel = $('.info-box .lory-slider')[0];
      if (!carousel) {
        return;
      }
      App.es6.shared.lory_carousel(carousel, null, { preloadSlides: 2 }, { onBeforeSlide: this.onBeforeSlide });
      App.es6.shared.wish_list.then(api => {
        api.updateDOMWithState();
      });
    },
    hideTooltip() {
      if (openedMarker.marker) {
        if (this.isMobile && this.mobileView === 'map') {
          this.unHighlightPin(openedMarker);
        }
      }
      if (this.infowindow) {
        this.infowindow.close();
      }
      openedMarker.marker = null;
      openedMarker.venueId = null;
      openedMarker.roomId = null;
    },
    ignoreTemporaryBoundsChange() {
      this.ignoreBoundsChange = true;
      setTimeout(() => {
        this.ignoreBoundsChange = false;
      }, 600);
    },
    getRoomData(roomId, callback) {
      getRoomData({
        roomId,
        roomTagSlug: this.eventTypeSlug,
      }, callback);
    },
    onBoundsChange() {
      this.$store.commit('setMapZoom', this.gMap.getZoom());
      this.onMove();
    },
    highlightPin(venueId, { bounce } = {}) {
      this.unHighlightPins();

      highlightedMarker.marker = addedVenuesMarkers[venueId];
      highlightedMarker.venueId = venueId;

      if (!highlightedMarker.marker) {
        return;
      }

      highlightedMarker.marker.zIndex = 9999;

      if (bounce) {
        highlightedMarker.marker.content.classList.add('map-pin-animate-bounce');
      }

      highlightedMarker.marker.content.src = ICONS.blue;
    },
    async updateMapBounds(bounds, enforceMinimumZoom) {
      if (enforceMinimumZoom) {
        this.gMap.setOptions({ maxZoom: this.MAX_ZOOM_FROM_LOCATION_INPUT });
        await sleep(1); // give it a moment to kick in
      }

      this.gMap.fitBounds(bounds, 0);

      if (enforceMinimumZoom) {
        this.gMap.setOptions({ maxZoom: this.MAX_ZOOM }); // restore
      }
    },
    unHighlightPins() {
      if (!highlightedMarker.marker && !openedMarker.marker) {
        return;
      }

      if (openedMarker.marker && openedMarker.venueId === highlightedMarker.venueId) {
        openedMarker.marker.content.classList.remove('map-pin-animate-bounce');
        return;
      }

      this.unHighlightPin(highlightedMarker);
    },
    unHighlightPin(marker) {
      if (!marker.marker) {
        return;
      }

      marker.marker.content.src = ICONS.red;

      marker.marker.zIndex = this.getVenuePinZIndex(marker.venueId);
      marker.marker.content.classList.remove('map-pin-animate-bounce');
    },
    resetPaintedPins() {
      PAINTED_PINS_BOUNDS = [];
    },
    initMap() {
      this.mapInitiated = true;
      const gMap = window.google.maps;

      this.gMap = new gMap.Map(this.$refs.mapContainer, {
        mapId: '5003409309071379',
        disableDefaultUI: true,
        zoomControl: false,
        clickableIcons: false,
        maxZoom: this.MAX_ZOOM,
        minZoom: 5,
        isFractionalZoomEnabled: false,
        gestureHandling: 'greedy',
      });

      const zoomControl = getZoomControl(this.gMap);
      this.gMap.controls[gMap.ControlPosition.RIGHT_TOP].push(zoomControl);
      window.__TGVGMap = this.gMap;

      // if map bounds have changed before calling this function switch to latest boundaries
      if (initialMapBounds) {
        this.updateMapBounds(initialMapBounds);
      }

      const onBoundsChangeDebounced = debounce(this.onBoundsChange, 400);

      this.gMap.addListener('zoom', this.resetPaintedPins);
      this.gMap.addListener('resize', this.resetPaintedPins);
      this.gMap.addListener('bounds_changed', () => {
        if (!this.$store.state.isMobile) {
          this.$store.dispatch('abortAjax');
        }
        onBoundsChangeDebounced();
      });

      this.gMap.addListener('idle', () => {
        gMap.event.clearListeners(this.gMap, 'idle');
        this.updatePoints(initialMapPoints, 'mapinit');
      });
      this.gMap.addListener('click', this.hideTooltip);

      this.infowindow = this.createInfoWindow();
    },
    createInfoWindow() {
      if (this.isMobile) {
        return new InfoWindow(this.$refs.infoWindowContainer, this.$refs.infoWindowContent);
      }

      return new window.google.maps.InfoWindow();
    },
    trackGoogleMapInit() {
      const mapInteracted = () => {
        this.$refs.searchMap.removeEventListener('mousedown', mapInteracted);
        this.$refs.searchMap.removeEventListener('touchstart', mapInteracted);
      };

      this.$refs.searchMap.addEventListener('mousedown', mapInteracted);
      this.$refs.searchMap.addEventListener('touchstart', mapInteracted);
    },
    loadMap() {
      this.loadMapTriggered = true;
      if (this.googleMapLoaded) {
        this.initMap();
      }
    },
    onGoogleMapLoaded() {
      this.googleMapLoaded = true;
      if (this.loadMapTriggered) {
        this.initMap();
      }
    },
    trackPinClick(point) {
      App.track.custom(window.__DATA_TRACKING_ENDPOINT_ROOMS, {
        event: 'map_pin_click',
        roomId: point.room_id,
        context: 'search_map',
        page: +this.page,
        tagSlug: this.eventTypeSlug,
      });

      this.$store.dispatch('trackGA4Interaction', {
        section: 'map',
        interaction: 'preview',
        element: 'room',
        elementValue: point.room_id,
      });

      visibilityTracker.addRoomDisplayed(point.room_id);
    },
    getCurrentCarouselImage() {
      return $('.info-box.results-item .lory-slide.active img');
    },
    getNextCarouselImageUrl(arrowDirection) {
      const $img = $('.info-box.results-item')
        .find('.lory-slide.active')[arrowDirection === 'left' ? 'prev' : 'next']().find('img');

      // if src was not yet set by lazy loading use data-src
      return $img.attr('src') || $img.data('src');
    },
    getCarouselContainer() {
      return $('.info-box.results-item .lory-slider');
    },
    onBeforeSlide(e) {
      const $slider = this.getCarouselContainer();
      const $img = this.getCurrentCarouselImage();
      const arrowDirection = e.detail.nextSlide > e.detail.index ? 'right' : 'left';
      const photoUrlAfter = this.getNextCarouselImageUrl(arrowDirection);

      App.track.roomCardCarouselArrowClick({
        roomId: $slider.data('room-id'),
        context: 'search_map',
        tagSlug: this.eventTypeSlug,
        position: 1,
        page: +this.page,
        photoIndex: $img.data('index') + 1,
        photoUrl: $img.attr('src'),
        totalPhotos: $slider.data('photos-count'),
        arrowDirection,
        photoUrlAfter,
      });

      this.$store.dispatch('trackGA4Interaction', {
        section: 'map',
        interaction: arrowDirection === 'left' ? 'photo_prev' : 'photo_next',
        element: 'room',
        elementValue: $slider.data('room-id'),
      });
    },
    trackMapTooltipClickEvent() {
      const $slider = this.getCarouselContainer();
      const $img = this.getCurrentCarouselImage();

      const roomId = $slider.data('room-id');
      const venueId = $slider.data('venue-id');

      if (!roomId) {
        throw new Error(`Room ID not found, got: ${roomId}`);
      }
      if (!venueId) {
        throw new Error(`Venue ID not found, got: ${venueId}`);
      }

      App.track.roomCardClick({
        roomId,
        context: 'search_map',
        tagSlug: this.eventTypeSlug,
        page: +this.page,
        position: 1,
        photoIndex: +$img.data('index') + 1,
        photoUrl: $img.attr('src'),
        totalPhotos: +$slider.data('photos-count'),
      });

      this.$store.dispatch('trackRoomClick', {
        roomId,
        venueId,
        section: 'map',
        element: 'room_tile',
        elementValue: roomId,
      });

      this.$store.dispatch('trackGA4Interaction', {
        section: 'map',
        interaction: 'click',
        element: 'room',
        elementValue: roomId,
      });

      visibilityTracker.addRoomClicked(roomId);
    },
    trackMapTooltipEvents() {
      App.es6.shared.track_click(
        document,
        this.trackMapTooltipClickEvent,
        { linkSelector: '.info-box.results-item a' },
      );
    },
    locateMe() {
      if (navigator.geolocation) {
        this.$store.commit('setLoading');
        this.infowindow.close();

        this.$store.dispatch('trackGA4Interaction', {
          section: 'map',
          element: 'current_location_button',
          interaction: 'click',
        });

        navigator.geolocation.getCurrentPosition((position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          this.gMap.setCenter(pos);
          this.gMap.setZoom(14);
        }, (error) => {
          console.info(error);

          this.$store.commit('setLoaded');
          this.isLocateMeError = true;

          setTimeout(() => {
            this.isLocateMeError = false;
          }, 3000);
        });
      }
    },
    closeInfoWindow() {
      this.infowindow.close();

      this.$store.dispatch('trackGA4Interaction', {
        section: 'map',
        element: 'room',
        elementValue: openedMarker.roomId,
        interaction: 'close',
      });
    },
    venuesInAreaClick() {
      this.$store.dispatch('showResults');

      this.$store.dispatch('trackGA4Interaction', {
        section: 'map',
        element: 'venues_in_area_button',
        interaction: 'click',
      });
    },
  },
  created() {
    $.subscribe('v.search.init', () => {
      if (this.loadMapTriggered) {
        return;
      }
      this.loadMap();
    });
    $.subscribe('v.search.points', ({ data, trigger }) => {
      if (this.mapInitiated) {
        this.updatePoints(data, trigger);
      } else {
        // if new points arrived but we haven't initiated the map yet
        // use new points as initialPoints when map will be rendered
        initialMapPoints = data;
      }
    });
    $.subscribe('v.search.locationChange', ({
      location,
      isMapResetTrigger = false,
      skipSearchRequest = false,
    }) => {
      if (isMapResetTrigger) {
        this.isMapResetTrigger = true;
      }

      const bounds = {
        west: location.longitude_from || location.west,
        east: location.longitude_to || location.east,
        south: location.latitude_from || location.south,
        north: location.latitude_to || location.north,
      };

      if (this.mapInitiated) {
        this.updateMapBounds(bounds, true);
      } else {
        initialMapBounds = bounds;
      }
      if (this.isMobile && !skipSearchRequest) {
        this.$store.dispatch('mapBoundariesChanged', {
          ...bounds,
          initial: false,
        });
      }
    });
    $.subscribe('v.search.highlightMarker', (venueId) => {
      if (this.mapInitiated) {
        this.highlightPin(venueId, {
          bounce: true,
        });
      }
    });
    $.subscribe('v.search.unHighlightMarker', () => {
      if (this.mapInitiated) {
        this.unHighlightPins();
      }
    });

    waitForGoogleMaps(this.onGoogleMapLoaded);
  },
  mounted() {
    window.reloadMapSettings = () => {
      SETTINGS.STEP_NR = window.STEP_NR || SETTINGS.STEP_NR;
      SETTINGS.MARKERS_IN_CELL = window.MARKERS_IN_CELL || SETTINGS.MARKERS_IN_CELL;
      SETTINGS.SHOW_EVERY = window.SHOW_EVERY || SETTINGS.SHOW_EVERY;
      SETTINGS.MINIMUM_ZOOM = window.MINIMUM_ZOOM || SETTINGS.MINIMUM_ZOOM;
    };

    this.trackMapTooltipEvents();
  },
};
</script>

<style lang="scss">
@use "@sass/base/variables";
@use "@sass/design-system/variables" as ds-variables;
@use 'SearchControls';

body {
  overflow-y: scroll;
}

@media (max-width: 700px) {
  .v-search-map {
    width: 100%;
  }
}

.v-search-map__map {
  width: 100%;
  height: 100%;
}

// remove blue border on hover introduced in v3.56.1 of google maps
.v-search-map__map > div > .gm-style > div {
  border: none !important;
}

.search-map-btn-refresh {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 10;
  border: 1px solid ds-variables.$grey-800;
  border-radius: 40px;
}

@keyframes map-pin-animate-bounce {
  0% {
    transform: translateY(0);
  }

  to {
    transform: translateY(-20px);
  }
}

.map-pin-animate-bounce {
  animation: map-pin-animate-bounce .4s ease infinite alternate;
}

</style>
